import { tagSetEntries } from './tags.js';
import { identityNodeId, } from './state.js';
/**
 * The tone Professionalism Training restricts the opener to when a package
 * declares no `pipLadder` of its own.
 */
export const CONSERVATIVE_TONE = 'conservative';
/**
 * Whether the difficulty profile admits `particle` into this run at all.
 *
 * The single place the `decoyTier` ceiling is applied. Every picker, the
 * starting particle pool, and — through `availableActions` — the solver's
 * assembly enumeration go through this one predicate, so a profile cannot
 * remove a decoy from the player's picker while leaving it in the search space
 * that proved the mission winnable.
 *
 * A particle a perk brought with it is authored at `decoyTier` 0 and is
 * therefore admitted at every profile: the player paid for it.
 */
export function admitsParticle(settings, particle) {
    return particle.decoyTier <= settings.decoyTierMax;
}
/**
 * Whether the picker shows this particle's tag chips.
 *
 * `all` is Intern and the Corporate Translator perk; `starter` is Analyst,
 * where the particles the player opened with are legible and everything earned
 * later has to be read from how the room reacts; `hidden` is Partner. The perk
 * reaches this through `settings.tagChips` like a profile does, so nothing here
 * asks which perks are equipped (design D10).
 */
export function chipsVisible(loaded, state, particleId) {
    const chips = state.settings.tagChips;
    if (chips === 'hidden')
        return false;
    if (chips === 'all')
        return true;
    return loaded.particles[particleId]?.source === 'starter';
}
/** The mission's slots, extended by the active puppet's persona slots, ascending by `n`. */
export function activeSlotDefs(loaded, identity) {
    const nodeId = identityNodeId(identity);
    const persona = nodeId === null ? [] : (loaded.puppets[nodeId]?.personaSlots ?? []);
    return [...loaded.pack.slots, ...persona].sort((left, right) => left.n - right.n);
}
/**
 * Rebuilds the composer for a new identity, keeping a selection only where the
 * new slot set still offers that slot with the same role. Switching to a puppet
 * therefore preserves the sentence the player was building, while switching
 * away drops anything that only existed in the persona.
 */
export function composerForIdentity(loaded, previous, identity) {
    const slots = activeSlotDefs(loaded, identity).map((slot) => {
        const kept = previous.slots.find((entry) => entry.n === slot.n && entry.role === slot.role);
        return {
            n: slot.n,
            role: slot.role,
            required: slot.required,
            particleId: kept?.particleId ?? null,
        };
    });
    return { slots, targetNodeId: previous.targetNodeId };
}
/** The slot definition at position `n`, or `undefined`. */
export function slotAt(state, n) {
    return state.composer.slots.find((slot) => slot.n === n);
}
/** Filled slots only, ascending by `n` — what a resolution is evaluated against. */
export function selectionOf(state) {
    const selection = [];
    for (const slot of state.composer.slots) {
        if (slot.particleId !== null)
            selection.push({ n: slot.n, particleId: slot.particleId });
    }
    return selection;
}
/** True while every `required` slot holds a particle — the only reason send is ever blocked. */
export function requiredSlotsFilled(state) {
    return state.composer.slots.every((slot) => !slot.required || slot.particleId !== null);
}
/** The `required` slots still empty, ascending. For the send button's accessible description. */
export function unfilledRequiredSlots(state) {
    return state.composer.slots
        .filter((slot) => slot.required && slot.particleId === null)
        .map((slot) => slot.n);
}
/** A particle's own tags include `axis: value`. */
const carries = (particle, axis, value) => tagSetEntries(particle.tags).some(([entryAxis, entryValue]) => entryAxis === axis && entryValue === value);
/**
 * Whether `particle` may be placed in slot `n` right now.
 *
 * Deliberately *not* a suitability test: this answers "is the interface allowed
 * to offer this?", never "would this work?".
 */
export function canSelect(loaded, state, n, particleId) {
    const slot = slotAt(state, n);
    if (slot === undefined)
        return false;
    const particle = loaded.particles[particleId];
    if (particle === undefined)
        return false;
    if (particle.slotRole !== slot.role)
        return false;
    if (!state.unlocked.particles.includes(particleId))
        return false;
    if (!admitsParticle(state.settings, particle))
        return false;
    // A puppet's exclusive particles exist only in that puppet's voice.
    if (particle.source.startsWith('puppet:')) {
        const owner = particle.source.slice('puppet:'.length);
        if (identityNodeId(state.identity) !== owner)
            return false;
    }
    // Professionalism Training: once a strike rung restricts the opener, that
    // slot offers particles carrying the required tone and nothing else.
    const requiredTone = state.pip.openerToneRequired;
    if (requiredTone !== null && slot.role === 'opener' && !carries(particle, 'tone', requiredTone)) {
        return false;
    }
    // The sentence is a space-join of the selected texts; the same particle twice
    // would read as a stutter, so a particle already placed is not offered again.
    return !state.composer.slots.some((entry) => entry.n !== n && entry.particleId === particleId);
}
/** Every particle the picker for slot `n` may offer, in pack declaration order. */
export function optionsForSlot(loaded, state, n) {
    return loaded.pack.particles
        .filter((particle) => canSelect(loaded, state, n, particle.id))
        .map((particle) => particle.id);
}
/**
 * The same options, ordered for a human rather than for the search.
 *
 * `optionsForSlot` is the *enumeration* order and must stay in the package's
 * declaration order, because `availableActions` is built from it and the solver
 * compares two runs' action lists for equality. This is the *presentation*
 * order, which a profile is allowed to weight: where a profile declares
 * `surfaceExtraDecoys`, the plausible-looking wrong answers are pushed to the
 * top of the sheet rather than merely being present in it.
 *
 * The sort is by descending `decoyTier` and is stable, so particles of equal
 * tier keep their authored order and the picker never reshuffles under the
 * player's hand.
 */
export function pickerOptionsForSlot(loaded, state, n) {
    const options = optionsForSlot(loaded, state, n);
    if (!state.settings.surfaceExtraDecoys)
        return options;
    return [...options].sort((left, right) => (loaded.particles[right]?.decoyTier ?? 0) - (loaded.particles[left]?.decoyTier ?? 0));
}
/** The assembled sentence: the selected texts, space-joined, in slot order. */
export function previewText(loaded, state) {
    return selectionOf(state)
        .map((slot) => loaded.particles[slot.particleId]?.text ?? '')
        .filter((text) => text.length > 0)
        .join(' ');
}
//# sourceMappingURL=composer.js.map